import type { GeneratedAsset, GeneratedAssetTool } from '@pulsify/shared/dto'
import { AlertTriangle, FileImage, ImageOff } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useGeneratedAssets } from '~/entities/generated-asset/model/use-generated-assets'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Card } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'
import {
  assetTitle,
  CHECKERBOARD,
  fileTypeLabel,
  formatCreatedAt,
  isImageAsset,
  toolLabel,
} from '../lib/asset-info'
import { AssetDetailDialog } from './asset-detail-dialog'

type ToolFilter = GeneratedAssetTool | 'all'

const SKELETON_KEYS = Array.from({ length: 8 }, (_, index) => `skeleton-${index}`)

export function MediaLibrary() {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: assets, isLoading, isError } = useGeneratedAssets(businessId)
  const [filter, setFilter] = useState<ToolFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const tools = useMemo(() => {
    const present = new Set((assets ?? []).map((asset) => asset.tool))
    return [...present]
  }, [assets])

  const activeFilter: ToolFilter = filter !== 'all' && tools.includes(filter) ? filter : 'all'
  const visible = useMemo(
    () =>
      activeFilter === 'all'
        ? (assets ?? [])
        : (assets ?? []).filter((asset) => asset.tool === activeFilter),
    [assets, activeFilter],
  )
  const selectedIndex = selectedId ? visible.findIndex((asset) => asset.id === selectedId) : -1

  function handleDeleted(assetId: string) {
    const index = visible.findIndex((asset) => asset.id === assetId)
    const neighbour = visible[index + 1] ?? visible[index - 1]
    setSelectedId(neighbour?.id ?? null)
  }

  const filterOptions = [
    { value: 'all' as const, label: m.mediaLibraryFilterAll() },
    ...tools.map((tool) => ({ value: tool, label: toolLabel(tool) })),
  ]

  return (
    <div className="space-y-6">
      <PageHeader title={m.mediaLibrary()} description={m.mediaLibraryDescription()} />

      {tools.length > 1 && (
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <SegmentedControl
            size="sm"
            label={m.mediaLibraryFilterLabel()}
            value={activeFilter}
            options={filterOptions}
            onChange={setFilter}
          />
        </div>
      )}

      {isLoading ? (
        <MediaGridSkeleton />
      ) : isError ? (
        <EmptyState icon={<AlertTriangle className="size-8" />} title={m.mediaLibraryLoadError()} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<ImageOff className="size-8" />}
          title={m.mediaLibraryEmpty()}
          hint={activeFilter === 'all' ? m.mediaLibraryEmptyHint() : m.mediaLibraryFilteredEmpty()}
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 2xl:grid-cols-5">
          {visible.map((asset) => (
            <li key={asset.id}>
              <MediaTile asset={asset} onOpen={() => setSelectedId(asset.id)} />
            </li>
          ))}
        </ul>
      )}

      <AssetDetailDialog
        businessId={businessId}
        assets={visible}
        index={selectedIndex}
        onIndexChange={(index) => setSelectedId(visible[index]?.id ?? null)}
        onClose={() => setSelectedId(null)}
        onDeleted={handleDeleted}
      />
    </div>
  )
}

function MediaTile({ asset, onOpen }: { asset: GeneratedAsset; onOpen: () => void }) {
  const title = assetTitle(asset)
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={m.mediaLibraryOpenDetails({ title })}
      className="group flex w-full flex-col gap-2 rounded-xl text-left outline-none"
    >
      <div
        className={cn(
          'relative aspect-square w-full overflow-hidden rounded-xl border transition-[border-color,box-shadow] duration-200',
          'group-hover:border-brand/40 group-hover:shadow-md group-focus-visible:border-ring group-focus-visible:ring-[3px] group-focus-visible:ring-ring/50',
          'motion-reduce:transition-none',
          CHECKERBOARD,
        )}
      >
        {isImageAsset(asset) ? (
          <img
            src={asset.url}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain p-2 transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <FileImage className="size-8" />
            <span className="text-xs font-medium">{fileTypeLabel(asset.mimeType)}</span>
          </div>
        )}
        <span className="absolute top-2 left-2 rounded-full border border-border/60 bg-background/85 px-2 py-0.5 text-[11px] font-medium text-foreground backdrop-blur-sm">
          {toolLabel(asset.tool)}
        </span>
      </div>
      <div className="min-w-0 px-0.5">
        <p className="truncate text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{formatCreatedAt(asset.createdAt, 'short')}</p>
      </div>
    </button>
  )
}

function MediaGridSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 2xl:grid-cols-5">
      {SKELETON_KEYS.map((key) => (
        <div key={key} className="flex flex-col gap-2">
          <Skeleton className="aspect-square w-full rounded-xl" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  )
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode
  title: string
  hint?: string
}) {
  return (
    <Card className="flex flex-col items-center gap-2 px-6 py-16 text-center">
      <div className="mb-1 flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon}
      </div>
      <p className="font-medium">{title}</p>
      {hint && <p className="max-w-sm text-sm text-muted-foreground">{hint}</p>}
    </Card>
  )
}
